import React, { ReactNode, useId, useState } from "react";
import {
    Box,
    Icon,
    Input,
    Text,
} from "@commonsku/design-system";
import { Search } from "lucide-react";
import styled from "styled-components";

export type SearchBarStatus = "idle" | "prompt" | "loading" | "results" | "empty" | "error";

export interface SearchBarProps {
    value: string;
    onValueChange: (value: string) => void;
    onSubmit: (value: string) => void;
    status: SearchBarStatus;
    children?: ReactNode;
    placeholder?: string;
    ariaLabel?: string;
    promptMessage?: string;
    loadingMessage?: string;
    emptyMessage?: string;
    errorMessage?: string;
    resultsAriaLabel?: string;
}

const SearchInput = styled(Input)`
    &&& {
        height: var(--input-height);
        margin: 0;
        padding-inline-start: 2.5rem;
        font-size: 0.875rem;
        border-color: var(--chakra-colors-border);
        border-radius: var(--chakra-radii-l2);
        box-shadow: none;
    }

    &&&:focus-visible {
        border-color: var(--focus-ring-color);
        outline: var(--focus-ring-width, 1px)
            var(--focus-ring-style, solid)
            var(--focus-ring-color);
        outline-offset: 0;
    }
`;

const statusMessage = (
    status: SearchBarStatus,
    {
        promptMessage,
        loadingMessage,
        emptyMessage,
        errorMessage,
    }: Pick<
        SearchBarProps,
        "promptMessage" | "loadingMessage" | "emptyMessage" | "errorMessage"
    >,
) => {
    switch (status) {
        case "prompt":
            return promptMessage;
        case "loading":
            return loadingMessage;
        case "empty":
            return emptyMessage;
        case "error":
            return errorMessage;
        default:
            return null;
    }
};

const SearchBar = ({
    value,
    onValueChange,
    onSubmit,
    status,
    children,
    placeholder = "Search",
    ariaLabel = "Search",
    promptMessage = "Enter more characters to search.",
    loadingMessage = "Searching...",
    emptyMessage = "No results found.",
    errorMessage = "Search is unavailable. Please try again.",
    resultsAriaLabel = "Search results",
}: SearchBarProps) => {
    const [hasFocus, setHasFocus] = useState(false);
    const dropdownId = useId();
    const message = statusMessage(status, {
        promptMessage,
        loadingMessage,
        emptyMessage,
        errorMessage,
    });
    const isOpen = hasFocus && 0 < value.trim().length && "idle" !== status;

    return (
        <Box
            role="search"
            position="relative"
            width="full"
            onFocusCapture={() => setHasFocus(true)}
            onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    setHasFocus(false);
                }
            }}
        >
            <Box
                position="absolute"
                insetStart="3"
                top="50%"
                transform="translateY(-50%)"
                display="flex"
                color="fg.muted"
                pointerEvents="none"
                zIndex="1"
            >
                <Icon ariaHidden size="md">
                    <Search />
                </Icon>
            </Box>
            <SearchInput
                type="search"
                size="sm"
                value={value}
                onChange={(event) => onValueChange(event.currentTarget.value)}
                onKeyDown={(event) => {
                    if ("Enter" !== event.key || event.nativeEvent.isComposing) {
                        return;
                    }

                    event.preventDefault();
                    onSubmit(value);
                }}
                placeholder={placeholder}
                aria-label={ariaLabel}
                aria-controls={isOpen ? dropdownId : undefined}
                aria-expanded={isOpen}
                autoComplete="off"
                background="bg.panel"
                _placeholder={{
                    color: "fg.placeholder",
                    opacity: "1",
                }}
            />
            {isOpen && (
                <Box
                    id={dropdownId}
                    role={"results" === status ? "region" : undefined}
                    aria-label={"results" === status ? resultsAriaLabel : undefined}
                    aria-busy={"loading" === status}
                    position="absolute"
                    top="calc(100% + 0.25rem)"
                    insetStart="0"
                    width="full"
                    maxHeight="30rem"
                    overflowY="auto"
                    display="flex"
                    flexDirection="column"
                    gap="2"
                    padding="1"
                    background="bg.panel"
                    borderRadius="l2"
                    boxShadow="lg"
                    zIndex="dropdown"
                >
                    {"results" === status ? children : (
                        <Text
                            role="status"
                            margin="0"
                            paddingX="4"
                            paddingY="3"
                            color={"error" === status ? "fg.critical" : "fg.muted"}
                        >
                            {message}
                        </Text>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default SearchBar;
